import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trpc } from '../../lib/api';
import EquipmentFormModal from '../EquipmentFormModal';
import { useCompanyStore } from '../../stores/companyStore';
import { useBreadcrumbTitle } from '../../hooks/useBreadcrumb';
import {
  App,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Button,
  Dropdown,
  Descriptions,
  Tag,
  Progress,
  Table,
  Statistic,
  Alert,
  Grid,
  Tabs,
  Switch,
  Tooltip
} from 'antd';
import type { MenuProps } from 'antd';
import { withDevModalFilePath } from '../common';

const { Modal } = withDevModalFilePath('client/src/components/equipment/EquipmentDetailView.tsx');
import dayjs from 'dayjs';
import { formatDateWithFallback } from '../../utils/dateFormat';
import { DateDisplay } from '../common';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  MoreOutlined,
  CloseOutlined,
  AppstoreOutlined,
  CopyOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { PermissionButton } from '../common/PermissionButton';
import { usePermission } from '../../hooks/usePermission';
import {
  getEnvPresetByKey,
  getMatPresetByKey,
} from '../../constants/deterioration-presets';
import { DEFAULT_EUL_TYPE, getEulTypeLabel, isEulType, type EulType } from '../../constants/eul-types';
import { getErrorMessage } from '../../utils/errorMessage';
import {
  calculateEnvironmentAdjustedLifespan,
  calculateLifespanBySelectedEulType,
} from '../../utils/deteriorationLifespan';
import { EquipmentDisplay, EquipmentCategoryDisplay, UserDisplay, MarkdownDisplay } from '../common';
import { EquipmentMediaGallery } from './EquipmentMediaGallery';
import { DocumentListSection } from '../document/DocumentListSection';
import { hasRequiredHealthParameters } from '../../pages/utils/healthParameterVisibility';

const { Title, Text } = Typography;

// タブとハッシュのマッピング
const TAB_HASH_MAP: Record<string, string> = {
  '1': 'basic',
  '2': 'operation',
  '3': 'health',
  '4': 'media',
  '5': 'schedule',
  '6': 'documents',
} as const;

const HASH_TAB_MAP: Record<string, string> = {
  'basic': '1',
  'operation': '2',
  'health': '3',
  'media': '4',
  'schedule': '5',
  'documents': '6',
} as const;

interface LocationTreeNode {
  id: string;
  name: string;
  children?: LocationTreeNode[] | null;
}

interface EquipmentLocationEntry {
  locationId: string;
}

type AssignedUserSummary = {
  id: string;
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  preferences?: Record<string, unknown> | null;
};

interface ScheduleHistoryRow {
  id?: string;
  scheduledDate?: string | null;
  title?: string | null;
  workType?: string | null;
  status?: string | null;
  assignedTo?: string | null;
  assignedUsers?: AssignedUserSummary[] | null;
  actualStart?: string | null;
}

type UserSummary = {
  id: string;
  name?: string | null;
  email?: string | null;
};

interface ChildEquipmentSummary {
  id: string;
  name: string;
  displayId?: string | null;
  importanceLevel?: number | null;
  currentDeteriorationRate?: string | number | null;
  dpEul?: string | number | null;
  dpBeta?: string | number | null;
  manufacturerLifespan?: string | number | null;
  commissionedDate?: string | Date | null;
  installationDate?: string | Date | null;
  startDate?: string | Date | null;
  isActive: boolean;
}

interface SupplierInfo {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  contactPerson?: string | null;
}

interface EquipmentDetail extends Record<string, unknown> {
  id: string;
  companyId: string;
  name: string;
  displayId?: string | null;
  parentId?: string | null;
  currentDeteriorationRate?: string | null;
  isActive?: boolean;
  manufacturerLifespan?: number | string | null;
  commissionedDate?: string | Date | null; // 稼働開始日（統合カラム）
  installationDate?: string | Date | null;
  startDate?: string | Date | null;
  lastMaintenanceDate?: string | Date | null;
  nextInspectionDate?: string | Date | null;
  importanceLevel?: number | null;
  dpEul?: string | null;
  dpBeta?: string | null;
  dpKEnv?: string | null;
  dpKMaint?: string | null;
  dpKMat?: string | null;
  dpEnvPreset?: string | null;
  dpMatPreset?: string | null;
  dpEquipmentType?: string | null;
  dpEulType?: string | null;
  dpEulInput?: string | null;
  price?: string | null;
  categoryId?: string | null;
  category?: unknown;
  categoryInfo?: {
    id: string;
    categoryLabel?: string | null;
    icon?: string | null;
    color?: string | null;
  } | null;
  manufacturer?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  fixedAssetNumber?: string | null;
  riskLevel?: number | null;
  manufacturerSupplier?: SupplierInfo | null;
  salesSupplier?: SupplierInfo | null;
  maintenanceSupplier?: SupplierInfo | null;
  notes?: string | null;
}

interface DeteriorationPredictionSummary {
  predictionDate: string | Date;
  predictedRate: number | string;
  healthIndex?: number | string | null;
  remainingLife?: number | string | null;
}

// Local Equipment interface removed (unused)

interface EquipmentDetailViewProps {
  equipmentId: string;
  mode?: 'page' | 'modal';
  onClose?: () => void;
}

const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPredictionYear = (value: string | Date): number | null => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.getFullYear();
};

const resolveCommissionedLikeDate = (input: {
  commissionedDate?: string | Date | null;
  installationDate?: string | Date | null;
  startDate?: string | Date | null;
}): Date | null => {
  const candidates = [input.commissionedDate, input.installationDate, input.startDate];
  for (const value of candidates) {
    if (!value) {
      continue;
    }
    const parsed = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return null;
};

export function EquipmentDetailView({ equipmentId, mode = 'page', onClose }: EquipmentDetailViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canManage, getDisabledMessage } = usePermission();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const utils = trpc.useContext();
  const screens = Grid.useBreakpoint();
  const baseColumnCount = screens.md || screens.lg || screens.xl || screens.xxl ? 2 : 1;
  const { message } = App.useApp();

  // モーダルモードの場合はハッシュを使用しない
  const shouldUseHash = mode !== 'modal';

  // ハッシュから初期タブを決定
  const getActiveTabFromHash = () => {
    if (!shouldUseHash) return '1';
    const hashValue = location.hash.replace('#', '');
    return HASH_TAB_MAP[hashValue] || '1';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromHash());

  // 健全度管理方式を取得
  const { healthManagementMode, setHealthManagementMode } = useCompanyStore();
  const { data: currentHealthMode } = trpc.company.getHealthManagementMode.useQuery();

  useEffect(() => {
    if (currentHealthMode) {
      setHealthManagementMode(currentHealthMode);
    }
  }, [currentHealthMode, setHealthManagementMode]);

  const isManufacturerLifespanMode = (currentHealthMode || healthManagementMode) === 'manufacturer_lifespan';

  // APIからデータ取得
  const { data: equipment, isLoading, error } = trpc.equipment.getById.useQuery(
    { id: equipmentId },
    { enabled: !!equipmentId }
  );
  const { data: predictionsResponse } = trpc.deterioration.predictForEquipment.useQuery(
    { equipmentId, yearsAhead: 10 },
    { enabled: !!equipmentId }
  );
  const { data: equipmentCategories = [] } = trpc.equipmentCategory.list.useQuery({
    includeInactive: true,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  // 親設備の情報（存在時のみ取得）
  const parentId = (equipment as EquipmentDetail | undefined)?.parentId ?? '';
  const { data: parentEquipment } = trpc.equipment.getById.useQuery(
    { id: parentId },
    { enabled: Boolean(parentId) }
  );

  // エリアデータの取得
  const { data: locationsTree } = trpc.location.getTree.useQuery();

  // 現在の設備に紐づくエリア取得
  const { data: equipmentLocations } = trpc.equipment.getLocationsByEquipmentId.useQuery(
    { equipmentId },
    { enabled: !!equipmentId }
  );

  // 子設備の取得
  const { data: childEquipments } = trpc.equipment.getChildrenByParentId.useQuery(
    { parentId: equipmentId },
    { enabled: !!equipmentId }
  );

  // 設備に紐づく保全スケジュールを取得（予定/実施分も含む）
  const { data: scheduleHistory } = trpc.schedule.getCalendarView.useQuery(
    {
      equipmentId,
      statuses: ['scheduled', 'in_progress', 'postponed', 'completed'],
      limit: 200,
    },
    { enabled: !!equipmentId }
  );

  const companyId = (equipment as EquipmentDetail | undefined)?.companyId ?? null;
  const { data: userQueryResult } = trpc.user.listDisplay.useQuery(
    { companyId: companyId ?? undefined, page: 1, limit: 200 },
    { enabled: Boolean(companyId) }
  );

  const parentEquipmentDetail = parentEquipment as EquipmentDetail | undefined;
  const locationNodes: LocationTreeNode[] = Array.isArray(locationsTree) ? (locationsTree as LocationTreeNode[]) : [];
  const equipmentLocationEntries: EquipmentLocationEntry[] = Array.isArray(equipmentLocations) ? (equipmentLocations as EquipmentLocationEntry[]) : [];
  const childEquipmentList: ChildEquipmentSummary[] = Array.isArray(childEquipments) ? (childEquipments as ChildEquipmentSummary[]) : [];
  const scheduleHistoryRows = useMemo<ScheduleHistoryRow[]>(() => {
    if (!Array.isArray(scheduleHistory)) {
      return [];
    }
    return scheduleHistory.map((row) => ({
      ...row,
      assignedUsers: (row?.assignedUsers ?? []).filter(
        (user): user is AssignedUserSummary => Boolean(user?.id)
      ),
    }));
  }, [scheduleHistory]);

  const userList = useMemo<UserSummary[]>(() => {
    if (!userQueryResult) {
      return [];
    }
    const fromData = (userQueryResult as { data?: UserSummary[] }).data;
    if (Array.isArray(fromData)) {
      return fromData;
    }
    const fromItems = (userQueryResult as { items?: UserSummary[] }).items;
    if (Array.isArray(fromItems)) {
      return fromItems;
    }
    return [];
  }, [userQueryResult]);

  const getUserLabel = (userItem: UserSummary) =>
    userItem.name ?? userItem.email ?? userItem.id;

  const userNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const userItem of userList) {
      if (userItem?.id) {
        map.set(userItem.id, getUserLabel(userItem));
      }
    }
    return map;
  }, [userList]);

  // パンくずリストに設備名を設定
  useBreadcrumbTitle('equipment', (equipment as EquipmentDetail | undefined)?.name);

  // 更新用のミューテーション
  const updateMutation = trpc.equipment.update.useMutation({
    onSuccess: () => {
      message.success('設備情報を更新しました');
      setIsEditModalVisible(false);
      utils.equipment.getById.invalidate({ id: equipmentId });
      utils.equipment.getLocationsByEquipmentId.invalidate({ equipmentId });
    },
    onError: (error: unknown) => {
      message.error(`更新に失敗しました: ${getErrorMessage(error, '不明なエラー')}`);
    }
  });

  // ステータス切り替えハンドラ
  const handleStatusToggle = async (checked: boolean) => {
    if (!equipment?.id) return;
    try {
      await updateMutation.mutateAsync({
        id: equipment.id,
        data: { isActive: checked }
      });
    } catch {
      // エラーはupdateMutationのonErrorで処理される
    }
  };

  const updateLocationsMutation = trpc.equipment.updateEquipmentLocations.useMutation({
    onSuccess: () => {
      message.success('エリア情報を更新しました');
    },
    onError: (error: unknown) => {
      message.error(getErrorMessage(error, 'エリア更新に失敗しました'));
    },
  });

  const deleteMutation = trpc.equipment.delete.useMutation({
    onSuccess: () => {
      message.success('設備を削除しました');
      utils.equipment.getListWithHierarchy.invalidate();
      utils.equipment.getById.invalidate({ id: equipmentId });
      if (mode === 'modal') {
        onClose?.();
        return;
      }
      navigate('/app/equipments');
    },
    onError: (error: unknown) => {
      message.error(getErrorMessage(error, '設備の削除に失敗しました'));
    },
  });

  const duplicateMutation = trpc.equipment.duplicate.useMutation({
    onSuccess: (data) => {
      message.success(`設備を複製しました: ${data.name}`);
      utils.equipment.getById.invalidate({ id: equipmentId });
      // 複製した設備の詳細ページへ遷移
      navigate(`/app/equipments/${data.id}`);
    },
    onError: (error: unknown) => {
      message.error(getErrorMessage(error, '複製に失敗しました'));
    },
  });

  const handleDelete = () => {
    if (!equipment?.id) {
      return;
    }
    const targetName = equipment.name || 'この設備';
    Modal.confirm({
      title: '設備の削除',
      content: `${targetName} を削除してもよろしいですか？`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      centered: true,
      onOk: async () => {
        return deleteMutation.mutateAsync({ id: equipment.id });
      },
    });
  };

  const actionMenuItems: MenuProps['items'] = [
    ...(mode === 'modal'
      ? [
        {
          key: 'openPage',
          label: '全画面で表示',
          icon: <AppstoreOutlined />,
        },
      ]
      : []),
    {
      key: 'delete',
      label: canManage ? '削除' : (
        <Tooltip title={getDisabledMessage('manage')}>
          <span>削除</span>
        </Tooltip>
      ),
      danger: canManage,
      icon: canManage ? <DeleteOutlined /> : <LockOutlined />,
      disabled: deleteMutation.isPending || !canManage,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'openPage') {
      if (mode === 'modal') {
        onClose?.();
      }
      navigate(`/app/equipments/${equipmentId}`);
      return;
    }
    if (key === 'delete') {
      handleDelete();
    }
  };

  const resolvedCategoryInfo = useMemo<EquipmentDetail['categoryInfo']>(() => {
    if (!equipment) {
      return null;
    }

    if (equipment.categoryInfo && typeof equipment.categoryInfo === 'object') {
      const info = equipment.categoryInfo;
      if (info.id) {
        return info;
      }
      if (info.categoryLabel) {
        return {
          ...info,
          id: info.categoryLabel,
        };
      }
      return info;
    }

    const rawCategory = (equipment as { category?: unknown }).category;

    if (rawCategory && typeof rawCategory === 'object') {
      const categoryObj = rawCategory as {
        id?: string | null;
        categoryLabel?: string | null;
        categoryKey?: string | null;
        icon?: string | null;
        color?: string | null;
      };

      const fallbackId = categoryObj.id ?? categoryObj.categoryKey ?? equipment.categoryId ?? null;

      if (fallbackId || categoryObj.categoryLabel || categoryObj.categoryKey) {
        return {
          id: fallbackId ?? categoryObj.categoryLabel ?? 'uncategorized',
          categoryLabel: categoryObj.categoryLabel ?? categoryObj.categoryKey ?? null,
          icon: categoryObj.icon ?? null,
          color: categoryObj.color ?? null,
        };
      }
    }

    const candidateValues: string[] = [];
    if (equipment.categoryId) {
      candidateValues.push(equipment.categoryId);
    }
    if (typeof rawCategory === 'string' && rawCategory.trim()) {
      candidateValues.push(rawCategory.trim());
    }

    if (candidateValues.length > 0 && equipmentCategories.length > 0) {
      const matchedCategory = equipmentCategories.find((categoryRecord) => {
        const label = (categoryRecord.categoryLabel ?? '').trim();
        const key = ((categoryRecord as { categoryKey?: string | null }).categoryKey ?? '').trim();
        return candidateValues.some((value) => {
          const normalized = value.trim();
          return (
            normalized.length > 0 &&
            (normalized === categoryRecord.id || normalized === key || normalized === label)
          );
        });
      });

      if (matchedCategory) {
        return {
          id: matchedCategory.id,
          categoryLabel: matchedCategory.categoryLabel ?? matchedCategory.id,
          icon: matchedCategory.icon ?? null,
          color: matchedCategory.color ?? null,
        };
      }
    }

    if (candidateValues.length > 0) {
      const fallbackValue = candidateValues[0];
      return {
        id: fallbackValue,
        categoryLabel: fallbackValue,
        icon: null,
        color: null,
      };
    }

    return null;
  }, [equipment, equipmentCategories]);

  // ハッシュ変更時のタブ更新
  useEffect(() => {
    // モーダルモードの場合はハッシュを監視しない
    if (!shouldUseHash) return;

    const hashValue = location.hash.replace('#', '');
    const targetTab = HASH_TAB_MAP[hashValue] || '1';
    if (targetTab !== activeTab) {
      setActiveTab(targetTab);
    }
  }, [location.hash, shouldUseHash, activeTab]);

  if (isLoading) {
    return (
      <Card loading={true}>
        <div style={{ height: 400 }}>読み込み中...</div>
      </Card>
    );
  }

  if (error || !equipment) {
    return (
      <Alert
        message="エラー"
        description="設備データの取得に失敗しました"
        type="error"
        action={
          <Button onClick={() => navigate(-1)}>戻る</Button>
        }
      />
    );
  }

  // エリア名を取得
  const getLocationNames = () => {
    if (!equipmentLocations || equipmentLocations.length === 0) return '未設定';
    if (!locationsTree || locationsTree.length === 0) return '未設定';

    const locationIds = equipmentLocationEntries.map((entry) => entry.locationId);
    const names: string[] = [];

    const findLocationName = (locations: LocationTreeNode[], id: string): string | null => {
      for (const loc of locations) {
        if (loc.id === id) return loc.name;
        if (loc.children) {
          const found = findLocationName(loc.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    locationIds.forEach((id: string) => {
      const name = findLocationName(locationNodes, id);
      if (name) names.push(name);
    });

    return names.length > 0 ? names.join(', ') : '未設定';
  };

  // 実データの整形
  const dpEulValue = equipment.dpEul ? parseFloat(equipment.dpEul) : 0;
  const dpBetaValue = equipment.dpBeta ? parseFloat(equipment.dpBeta) : 0;
  const commissionedLikeDate = resolveCommissionedLikeDate({
    commissionedDate: equipment.commissionedDate,
    installationDate: equipment.installationDate,
    startDate: (equipment as EquipmentDetail).startDate,
  });
  const hasCommissionedLikeDate = commissionedLikeDate !== null;
  const hasHealthParams = hasRequiredHealthParameters(
    isManufacturerLifespanMode ? 'manufacturer_lifespan' : 'complex',
    {
      dpEul: equipment.dpEul,
      dpBeta: equipment.dpBeta,
      manufacturerLifespan: equipment.manufacturerLifespan,
      commissionedDate: equipment.commissionedDate,
      installationDate: equipment.installationDate,
      startDate: (equipment as EquipmentDetail).startDate,
    },
  );
  const manufacturerLifespanYears = toFiniteNumber(equipment.manufacturerLifespan);
  const missingComplexParams: string[] = [];
  if (!(dpEulValue > 0)) {
    missingComplexParams.push('メーカー提供の耐用年数');
  }
  if (!(dpBetaValue > 0)) {
    missingComplexParams.push('設備タイプ');
  }
  if (!hasCommissionedLikeDate) {
    missingComplexParams.push('稼働開始日');
  }

  const predictions: DeteriorationPredictionSummary[] = Array.isArray(predictionsResponse)
    ? predictionsResponse as DeteriorationPredictionSummary[]
    : [];
  const currentYear = new Date().getFullYear();
  const currentPrediction = predictions.find(
    (prediction) => toPredictionYear(prediction.predictionDate) === currentYear
  ) ?? predictions[0] ?? null;
  const currentPredictedRate = toFiniteNumber(currentPrediction?.predictedRate);
  const currentPredictedHealth = toFiniteNumber(currentPrediction?.healthIndex)
    ?? (currentPredictedRate !== null ? 100 - currentPredictedRate : null);

  const parsedDeteriorationRate =
    equipment.currentDeteriorationRate !== null && equipment.currentDeteriorationRate !== undefined
      ? parseFloat(equipment.currentDeteriorationRate)
      : NaN;
  const fallbackDeteriorationRate = Number.isNaN(parsedDeteriorationRate) ? null : parsedDeteriorationRate;
  const effectiveDeteriorationRate =
    currentPredictedRate
    ?? (currentPredictedHealth !== null ? 100 - currentPredictedHealth : null)
    ?? fallbackDeteriorationRate;
  const hasHealthValue = hasHealthParams && effectiveDeteriorationRate !== null;

  const equipmentData = {
    ...equipment,
    categoryInfo: resolvedCategoryInfo,
    deteriorationRate: hasHealthValue ? effectiveDeteriorationRate : null,
    status: equipment.isActive ? 'operating' : 'stopped',
    riskLevel: typeof equipment.importanceLevel === 'number' ? equipment.importanceLevel : null,
    installationDate: commissionedLikeDate
      ? commissionedLikeDate.toLocaleDateString('ja-JP')
      : '-',
    nextMaintenanceDate: equipment.nextInspectionDate
      ? new Date(equipment.nextInspectionDate).toLocaleDateString('ja-JP')
      : '-',
    lastMaintenanceDate: equipment.lastMaintenanceDate
      ? new Date(equipment.lastMaintenanceDate).toLocaleDateString('ja-JP')
      : '-',
    expectedLifeYears: equipment.dpEul ? parseFloat(equipment.dpEul) : null,
    manufacturerEul: equipment.dpEulInput ? parseFloat(equipment.dpEulInput) : null,
    eulType: isEulType(equipment.dpEulType) ? equipment.dpEulType : DEFAULT_EUL_TYPE,
    deteriorationFactor: equipment.dpBeta ? parseFloat(equipment.dpBeta) : null,
    environmentalFactor: equipment.dpKEnv ? parseFloat(equipment.dpKEnv) : null,
    usageFactor: equipment.dpKMaint ? parseFloat(equipment.dpKMaint) : null,
    location: getLocationNames()
  };

  const selectedEulType: EulType = equipmentData.eulType;
  const environmentAdjustedLifespanLabel =
    selectedEulType === 'UNKNOWN'
      ? '環境整備耐用年数(B値)'
      : `環境整備耐用年数(${selectedEulType})`;
  const manufacturerProvidedLifespanYears =
    typeof equipmentData.manufacturerEul === 'number' && equipmentData.manufacturerEul > 0
      ? roundToTwoDecimals(equipmentData.manufacturerEul)
      : null;

  const betaForEulCalculation =
    typeof equipmentData.deteriorationFactor === 'number' && Number.isFinite(equipmentData.deteriorationFactor)
      ? equipmentData.deteriorationFactor
      : 2;

  const calculatedLifespanBySelectedTypeYears = calculateLifespanBySelectedEulType(
    typeof equipmentData.expectedLifeYears === 'number' ? equipmentData.expectedLifeYears : null,
    selectedEulType,
    betaForEulCalculation,
  );
  const environmentAdjustedLifespanYears = calculateEnvironmentAdjustedLifespan(
    calculatedLifespanBySelectedTypeYears ?? manufacturerProvidedLifespanYears,
    equipmentData.environmentalFactor,
    equipmentData.usageFactor,
  );

  const elapsedYearsFromCommissionedDate = (() => {
    if (!commissionedLikeDate) {
      return null;
    }
    return Math.max(0, (Date.now() - commissionedLikeDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  })();

  const remainingLifespanYearsByInput =
    environmentAdjustedLifespanYears !== null && elapsedYearsFromCommissionedDate !== null
      ? roundToTwoDecimals(environmentAdjustedLifespanYears - elapsedYearsFromCommissionedDate)
      : null;
  const remainingLifespanYears = remainingLifespanYearsByInput;

  const healthPercent =
    typeof equipmentData.deteriorationRate === 'number'
      ? Math.max(0, Math.min(100, roundToTwoDecimals(100 - equipmentData.deteriorationRate)))
      : null;

  const getRiskColor = (level: number | null) => {
    switch (level) {
      case 1: return 'green';
      case 2: return 'orange';
      case 3: return 'red';
      case 4: return 'purple';
      default: return 'default';
    }
  };

  const getRiskText = (level: number | null) => {
    switch (level) {
      case 1: return '低';
      case 2: return '中';
      case 3: return '高';
      case 4: return '要対応';
      default: return '-';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operating': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'maintenance': return <ToolOutlined style={{ color: '#faad14' }} />;
      case 'stopped': return <WarningOutlined style={{ color: '#ff4d4f' }} />;
      default: return null;
    }
  };



  const columns = [
    {
      title: '日付',
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      render: (date: string) => formatDateWithFallback(date),
    },
    {
      title: 'タイプ',
      dataIndex: 'workType',
      key: 'workType',
      render: (workType: string) => {
        const typeMap: Record<string, string> = {
          inspection: '点検',
          repair: '修理',
          replacement: '交換',
          calibration: '校正',
          cleaning: '清掃',
          adjustment: '調整',
          preventive: '予防保全',
          other: 'その他',
        };
        return typeMap[workType] || workType;
      },
    },
    {
      title: '内容',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig: Record<string, { color: string; label: string }> = {
          completed: { color: 'green', label: '完了' },
          scheduled: { color: 'blue', label: '予定' },
          in_progress: { color: 'orange', label: '作業中' },
          pending_approval: { color: 'gold', label: '承認待ち' },
          cancelled: { color: 'default', label: 'キャンセル' },
          postponed: { color: 'purple', label: '延期' },
        };
        const config = statusConfig[status] || { color: 'default', label: status };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '結果',
      dataIndex: 'result',
      key: 'result',
      render: (result: string) => {
        if (!result) return '-';
        const resultConfig: Record<string, { color: string; label: string }> = {
          passed: { color: 'green', label: '合格' },
          failed: { color: 'red', label: '不合格' },
          partial: { color: 'orange', label: '一部不合格' },
          pending: { color: 'blue', label: '判定待ち' },
        };
        const config = resultConfig[result] || { color: 'default', label: result };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
  ];

  // タブ変更時のハッシュ更新
  const handleTabChange = (key: string) => {
    setActiveTab(key);

    // モーダルモードの場合はハッシュを更新しない
    if (!shouldUseHash) return;

    const hash = TAB_HASH_MAP[key];
    if (hash) {
      // replace: true でブラウザ履歴を汚さない
      navigate(`#${hash}`, { replace: true });
    }
  };

  const handleEdit = () => {
    setIsEditModalVisible(true);
  };


  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={mode === 'modal' ? <CloseOutlined /> : <ArrowLeftOutlined />}
          onClick={() => {
            if (mode === 'modal') {
              onClose?.();
            } else {
              navigate(-1);
            }
          }}
        >
          {mode === 'modal' ? '閉じる' : '戻る'}
        </Button>
      </Space>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>{(equipmentData && equipmentData.name) ? `${equipmentData.name} 詳細` : '設備詳細'}</Title>
        <Space size={8}>
          <PermissionButton
            icon={<CopyOutlined />}
            aria-label="複製"
            onClick={() => duplicateMutation.mutate({ id: equipmentId })}
            loading={duplicateMutation.isPending}
            style={{
              height: 40,
              paddingInline: 16,
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 6,
            }}
            requiredPermission="edit"
          >
            複製
          </PermissionButton>
          <PermissionButton
            type="primary"
            icon={<EditOutlined />}
            aria-label="編集"
            onClick={handleEdit}
            style={{
              height: 40,
              paddingInline: 16,
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 6,
            }}
            requiredPermission="edit"
          >
            編集
          </PermissionButton>
          <Dropdown
            menu={{ items: actionMenuItems, onClick: handleMenuClick }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="default"
              icon={<MoreOutlined />}
              aria-label="その他の操作"
              loading={deleteMutation.isPending}
              style={{
                borderColor: '#d9d9d9',
                width: 40,
                height: 40,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
              }}
            />
          </Dropdown>
        </Space>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: '1',
            label: '基本情報',
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* 識別情報 */}
                <Card title="識別情報">
                  <Descriptions column={baseColumnCount} bordered>
                    <Descriptions.Item label="設備名">{equipmentData.name}</Descriptions.Item>
                    <Descriptions.Item label="カテゴリー">
                      {equipmentData.categoryInfo ? (
                        <EquipmentCategoryDisplay
                          category={{
                            id: equipmentData.categoryInfo.id,
                            categoryLabel: equipmentData.categoryInfo.categoryLabel ?? null,
                            icon: equipmentData.categoryInfo.icon ?? null,
                            color: equipmentData.categoryInfo.color ?? null
                          }}
                        />
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="型番">{equipmentData.model || '-'}</Descriptions.Item>
                    <Descriptions.Item label="シリアル番号">{equipmentData.serialNumber || '-'}</Descriptions.Item>
                    <Descriptions.Item label="固定資産番号">{equipmentData.fixedAssetNumber || '-'}</Descriptions.Item>
                    <Descriptions.Item label="導入価格">{equipment.price ? `¥${parseInt(equipment.price).toLocaleString()}` : '-'}</Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* 稼働状態 */}
                <Card title="稼働状態">
                  <Descriptions column={baseColumnCount} bordered>
                    <Descriptions.Item label="ステータス">
                      <Space>
                        <Switch
                          checked={equipment.isActive}
                          onChange={handleStatusToggle}
                          loading={updateMutation.isPending}
                          checkedChildren="稼働中"
                          unCheckedChildren="停止"
                        />
                        {getStatusIcon(equipmentData.status)}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="重要度">
                      <Tag color={getRiskColor(equipmentData.riskLevel)}>
                        {getRiskText(equipmentData.riskLevel)}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="稼働開始日">{equipmentData.installationDate}</Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* 取引先情報 */}
                <Card title="取引先情報">
                  <Descriptions column={baseColumnCount} bordered>
                    <Descriptions.Item label="メーカー業者">
                      {equipment.manufacturerSupplier ? (
                        <Space direction="vertical" size={0}>
                          <Button
                            type="link"
                            onClick={() => navigate(`/app/suppliers/${equipment.manufacturerSupplier?.id}`)}
                            style={{ padding: 0, fontWeight: 'bold' }}
                          >
                            {equipment.manufacturerSupplier.name}
                          </Button>
                          {equipment.manufacturerSupplier.phone && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              電話: {equipment.manufacturerSupplier.phone}
                            </Text>
                          )}
                        </Space>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="販売業者">
                      {equipment.salesSupplier ? (
                        <Space direction="vertical" size={0}>
                          <Button
                            type="link"
                            onClick={() => navigate(`/app/suppliers/${equipment.salesSupplier?.id}`)}
                            style={{ padding: 0, fontWeight: 'bold' }}
                          >
                            {equipment.salesSupplier.name}
                          </Button>
                          {equipment.salesSupplier.phone && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              電話: {equipment.salesSupplier.phone}
                            </Text>
                          )}
                        </Space>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="メンテナンス業者" span={baseColumnCount}>
                      {equipment.maintenanceSupplier ? (
                        <Space direction="vertical" size={0}>
                          <Button
                            type="link"
                            onClick={() => navigate(`/app/suppliers/${equipment.maintenanceSupplier?.id}`)}
                            style={{ padding: 0, fontWeight: 'bold' }}
                          >
                            {equipment.maintenanceSupplier.name}
                          </Button>
                          {equipment.maintenanceSupplier.phone && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              電話: {equipment.maintenanceSupplier.phone}
                            </Text>
                          )}
                        </Space>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* 詳細・備考 */}
                <Card title="詳細・備考">
                  {equipment?.notes ? (
                    <MarkdownDisplay content={equipment.notes} />
                  ) : (
                    <Text type="secondary">備考なし</Text>
                  )}
                </Card>

                {/* 配置情報カード */}
                <Card title="配置情報">
                  <Descriptions column={1} bordered>
                    {equipmentData.location !== '未設定' ? (
                      <Descriptions.Item label="配置エリア">
                        {equipmentData.location}
                      </Descriptions.Item>
                    ) : parentEquipment ? (
                      <Descriptions.Item label="親設備">
                        <EquipmentDisplay
                          equipment={{
                            id: parentEquipmentDetail?.id ?? '',
                            name: parentEquipmentDetail?.name ?? null,
                            code: null,
                            icon: null
                          }}
                        />
                      </Descriptions.Item>
                    ) : (
                      <Descriptions.Item label="配置">
                        未設定
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  {/* 子設備リスト */}
                  {childEquipments && childEquipments.length > 0 && (
                    <>
                      <div style={{ marginTop: 16, marginBottom: 8 }}>
                        <Text strong>子設備一覧 ({childEquipments.length}件)</Text>
                      </div>
                      <Table
                        size="small"
                        pagination={false}
                        columns={[
                          {
                            title: '設備名',
                            dataIndex: 'name',
                            key: 'name',
                            render: (text: string, record: ChildEquipmentSummary) => (
                              <EquipmentDisplay
                                equipment={{
                                  id: record.id,
                                  name: record.name ?? null,
                                  code: null,
                                  icon: null
                                }}
                                showIcon={false}
                              />
                            )
                          },
                          {
                            title: '重要度',
                            dataIndex: 'importanceLevel',
                            key: 'importanceLevel',
                            width: 100,
                            render: (level: number | null) => (
                              <Tag color={getRiskColor(level)}>
                                {getRiskText(level)}
                              </Tag>
                            )
                          },
                          {
                            title: '健全度',
                            dataIndex: 'currentDeteriorationRate',
                            key: 'currentDeteriorationRate',
                            width: 80,
                            render: (rate: string | number | null, record: ChildEquipmentSummary) => {
                              const hasRequiredParams = hasRequiredHealthParameters(
                                isManufacturerLifespanMode ? 'manufacturer_lifespan' : 'complex',
                                {
                                  dpEul: record.dpEul,
                                  dpBeta: record.dpBeta,
                                  manufacturerLifespan: record.manufacturerLifespan,
                                  commissionedDate: record.commissionedDate,
                                  installationDate: record.installationDate,
                                  startDate: record.startDate,
                                },
                              );
                              if (!hasRequiredParams) return <span style={{ color: '#999' }}>???%</span>;
                              if (rate === null || rate === undefined || rate === '') {
                                return <span style={{ color: '#999' }}>???%</span>;
                              }
                              const numRate = Number(rate);
                              if (!Number.isFinite(numRate)) return <span style={{ color: '#999' }}>???%</span>;
                              const healthIndex = 100 - numRate;
                              return (
                                <span style={{
                                  color: healthIndex < 30 ? '#ff4d4f' : healthIndex < 50 ? '#faad14' : '#52c41a'
                                }}>
                                  {healthIndex.toFixed(1)}%
                                </span>
                              );
                            }
                          },
                          {
                            title: 'ステータス',
                            dataIndex: 'isActive',
                            key: 'isActive',
                            width: 80,
                            render: (isActive: boolean) => (
                              <Tag color={isActive ? 'green' : 'red'}>
                                {isActive ? '稼働中' : '停止'}
                              </Tag>
                            )
                          }
                        ]}
                        dataSource={childEquipmentList.map((item, index) => ({
                          ...item,
                          key: item.id || index
                        }))}
                      />
                    </>
                  )}
                </Card>

                {/* 管理情報 */}
                <Card title="管理情報">
                  <Descriptions column={baseColumnCount} bordered>
                    <Descriptions.Item label="作成者">
                      {equipment?.createdByUser ? (
                        <UserDisplay user={equipment.createdByUser} />
                      ) : (
                        <Typography.Text type="secondary">不明</Typography.Text>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="最終編集者">
                      {equipment?.updatedByUser ? (
                        <UserDisplay user={equipment.updatedByUser} />
                      ) : (
                        <Typography.Text type="secondary">未編集</Typography.Text>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="作成日時">
                      <DateDisplay value={equipment?.createdAt} showTime />
                    </Descriptions.Item>
                    <Descriptions.Item label="最終更新日時">
                      <DateDisplay value={equipment?.updatedAt} showTime />
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Space>
            ),
          },
          {
            key: '2',
            label: '運転情報',
            children: (
              <Card>
                <Table
                  columns={columns}
                  dataSource={scheduleHistoryRows}
                  pagination={{
                    total: scheduleHistoryRows.length,
                    pageSize: 10,
                    showSizeChanger: false,
                    showTotal: (total) => `全${total}件`,
                  }}
                  size="small"
                  loading={!scheduleHistory}
                />
              </Card>
            ),
          },
          {
            key: '3',
            label: '健全度予測',
            children: isManufacturerLifespanMode ? (
              // メーカー耐用年数Ver: シンプル表示
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <Alert
                    message="耐用年数モード"
                    description="メーカー指定の耐用年数を基準に管理します。詳細な健全度予測が必要な場合は、会社設定から「健全度モード」に変更してください。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="耐用年数情報">
                    <Descriptions column={1} bordered>
                      <Descriptions.Item label="メーカー耐用年数">
                        {manufacturerLifespanYears !== null
                          ? `${manufacturerLifespanYears.toFixed(1)}年`
                          : '未設定'}
                      </Descriptions.Item>
                      <Descriptions.Item label="稼働開始日">
                        {commissionedLikeDate ? commissionedLikeDate.toLocaleDateString('ja-JP') : '未設定'}
                      </Descriptions.Item>
                      <Descriptions.Item label="経過年数">
                        {elapsedYearsFromCommissionedDate !== null
                          ? `${elapsedYearsFromCommissionedDate.toFixed(1)}年`
                          : '未設定'}
                      </Descriptions.Item>
                      <Descriptions.Item label="残存年数">
                        {(() => {
                          if (manufacturerLifespanYears === null || manufacturerLifespanYears <= 0) {
                            return '計算不可';
                          }
                          if (elapsedYearsFromCommissionedDate === null) {
                            return '計算不可';
                          }
                          const remaining = manufacturerLifespanYears - elapsedYearsFromCommissionedDate;
                          if (remaining <= 0) {
                            return <Tag color="red">耐用年数超過</Tag>;
                          }
                          const color = remaining < 2 ? 'red' : remaining < 5 ? 'orange' : 'green';
                          return <Tag color={color}>{remaining.toFixed(1)}年</Tag>;
                        })()}
                      </Descriptions.Item>
                      <Descriptions.Item label="ステータス">
                        {(() => {
                          if (manufacturerLifespanYears === null || manufacturerLifespanYears <= 0) {
                            return <Tag>未設定</Tag>;
                          }
                          if (elapsedYearsFromCommissionedDate === null) {
                            return <Tag>未設定</Tag>;
                          }
                          const ratio = elapsedYearsFromCommissionedDate / manufacturerLifespanYears;
                          if (ratio >= 1.0) return <Tag color="red" icon={<WarningOutlined />}>要交換</Tag>;
                          if (ratio >= 0.8) return <Tag color="orange">交換検討</Tag>;
                          return <Tag color="green" icon={<CheckCircleOutlined />}>良好</Tag>;
                        })()}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="推奨対応">
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                      {(() => {
                        if (manufacturerLifespanYears === null || manufacturerLifespanYears <= 0) {
                          return (
                            <Alert
                              message="メーカー耐用年数が未設定です"
                              description="メーカー耐用年数を設定すると、耐用年数の管理が可能になります。"
                              type="warning"
                              showIcon
                            />
                          );
                        }
                        if (elapsedYearsFromCommissionedDate === null) {
                          return (
                            <Alert
                              message="稼働開始日が未設定です"
                              description="稼働開始日を設定すると、耐用年数の管理が可能になります。"
                              type="warning"
                              showIcon
                            />
                          );
                        }
                        const ratio = elapsedYearsFromCommissionedDate / manufacturerLifespanYears;

                        if (ratio >= 1.0) {
                          return (
                            <Alert
                              message="即時交換を推奨"
                              description="メーカー耐用年数を超過しています。設備の交換を検討してください。"
                              type="error"
                              showIcon
                              icon={<WarningOutlined />}
                            />
                          );
                        }
                        if (ratio >= 0.8) {
                          return (
                            <Alert
                              message="交換計画の策定を推奨"
                              description="耐用年数の80%を超えています。計画的な交換を検討してください。"
                              type="warning"
                              showIcon
                            />
                          );
                        }
                        return (
                          <Alert
                            message="良好"
                            description="通常の保守・点検を継続してください。"
                            type="success"
                            showIcon
                            icon={<CheckCircleOutlined />}
                          />
                        );
                      })()}
                    </Space>
                  </Card>
                </Col>
              </Row>
            ) : (
              // 複雑Ver: 既存の詳細表示
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                  <Card style={{ marginTop: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                      <div>
                        <Text type="secondary">健全度</Text>
                        {healthPercent !== null ? (
                          <Progress
                            percent={healthPercent}
                            format={(percent) => `${(percent ?? 0).toFixed(2)}%`}
                            strokeColor={{
                              '0%': '#ff4d4f',
                              '50%': '#faad14',
                              '100%': '#52c41a'
                            }}
                          />
                        ) : (
                          <div style={{ color: '#999' }}>
                            <div>算出条件不足</div>
                            {!isManufacturerLifespanMode && missingComplexParams.length > 0 && (
                              <div style={{ fontSize: 12, marginTop: 4 }}>
                                不足項目: {missingComplexParams.join('、')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <Statistic
                        title="メーカー提供耐用年数"
                        value={
                          manufacturerProvidedLifespanYears !== null
                            ? manufacturerProvidedLifespanYears.toFixed(2)
                            : '-'
                        }
                        suffix={manufacturerProvidedLifespanYears !== null ? '年' : ''}
                      />
                      <Statistic
                        title={environmentAdjustedLifespanLabel}
                        value={
                          environmentAdjustedLifespanYears !== null
                            ? environmentAdjustedLifespanYears.toFixed(2)
                            : '-'
                        }
                        suffix={environmentAdjustedLifespanYears !== null ? '年' : ''}
                      />
                      <Statistic
                        title="残存耐用年数"
                        value={
                          remainingLifespanYears !== null
                            ? Math.max(remainingLifespanYears, 0).toFixed(2)
                            : '-'
                        }
                        suffix={remainingLifespanYears !== null ? '年' : ''}
                      />
                      {remainingLifespanYears !== null && remainingLifespanYears < 0 && (
                        <Text type="danger" style={{ fontSize: 12 }}>
                          耐用年数を {Math.abs(remainingLifespanYears).toFixed(2)} 年超過しています。
                        </Text>
                      )}
                      {elapsedYearsFromCommissionedDate === null && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          稼働開始日が未設定のため、残存耐用年数は計算できません。
                        </Text>
                      )}


                      <Button
                        type="primary"
                        block
                        onClick={() => navigate(`/app/deterioration/${equipmentData.id}`)}
                      >
                        詳細な健全度予測を見る
                      </Button>
                    </Space>
                  </Card>
                </Col>

                <Col xs={24} lg={8}>
                  <Card>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="耐用年数指標">
                        {getEulTypeLabel(equipmentData.eulType)}
                      </Descriptions.Item>
                      <Descriptions.Item label="メーカー提供耐用年数">
                        {manufacturerProvidedLifespanYears !== null
                          ? `${manufacturerProvidedLifespanYears.toFixed(2)} 年`
                          : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label={environmentAdjustedLifespanLabel}>
                        {environmentAdjustedLifespanYears !== null
                          ? `${environmentAdjustedLifespanYears.toFixed(2)} 年`
                          : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="残存耐用年数">
                        {remainingLifespanYears === null
                          ? '-'
                          : remainingLifespanYears < 0
                            ? `耐用年数超過 ${Math.abs(remainingLifespanYears).toFixed(2)} 年`
                            : `${remainingLifespanYears.toFixed(2)} 年`}
                      </Descriptions.Item>
                      <Descriptions.Item label="計算メモ">
                        {`B値: ${selectedEulType} / β=${betaForEulCalculation.toFixed(2)} / kEnv=${(equipmentData.environmentalFactor ?? 1).toFixed(2)} / kMaint=${(equipmentData.usageFactor ?? 1).toFixed(2)}`}
                      </Descriptions.Item>
                      <Descriptions.Item label="設備タイプ">
                        {equipment.dpEquipmentType || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="設置環境">
                        {(() => {
                          if (equipment.dpEnvPreset) {
                            const preset = getEnvPresetByKey(equipment.dpEnvPreset);
                            return preset ? `${preset.label}（${preset.description}）` : equipment.dpEnvPreset;
                          }
                          if (equipmentData.environmentalFactor) {
                            return `マニュアル設定: ${equipmentData.environmentalFactor}`;
                          }
                          return '-';
                        })()}
                      </Descriptions.Item>
                      <Descriptions.Item label="材質">
                        {(() => {
                          if (equipment.dpMatPreset) {
                            const preset = getMatPresetByKey(equipment.dpMatPreset);
                            return preset ? `${preset.label}（${preset.description}）` : equipment.dpMatPreset;
                          }
                          if (equipmentData.dpKMat) {
                            return `マニュアル設定: ${parseFloat(equipmentData.dpKMat)}`;
                          }
                          return '-';
                        })()}
                      </Descriptions.Item>
                      <Descriptions.Item label="メンテナンス係数">
                        {equipmentData.usageFactor || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="建設係数">
                        {equipmentData.dpKConst ? parseFloat(equipmentData.dpKConst) : '-'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            )
          },
          {
            key: '4',
            label: '画像',
            children: (
              <EquipmentMediaGallery equipmentId={equipmentId} editable={true} />
            ),
          },
          {
            key: '5',
            label: '保全スケジュール',
            children: (
              <Card>
                <Table
                  size="small"
                  dataSource={scheduleHistoryRows}
                  pagination={{
                    total: scheduleHistoryRows.length,
                    pageSize: 10,
                    showSizeChanger: false,
                    showTotal: (total) => `全 ${total} 件`,
                  }}
                  loading={!scheduleHistory}
                  columns={[
                    {
                      title: '予定日',
                      dataIndex: 'scheduledDate',
                      key: 'scheduledDate',
                      width: 120,
                      render: (date: string) => formatDateWithFallback(date),
                      sorter: (a: ScheduleHistoryRow, b: ScheduleHistoryRow) => {
                        const aTime = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
                        const bTime = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
                        return bTime - aTime;
                      },
                      defaultSortOrder: 'descend',
                    },
                    {
                      title: 'タイトル',
                      dataIndex: 'title',
                      key: 'title',
                      render: (text: string, record: ScheduleHistoryRow) => (
                        <Button
                          type="link"
                          onClick={() => navigate(`/app/schedule/${record.id}`)}
                          style={{ padding: 0, fontWeight: 'bold' }}
                        >
                          {text}
                        </Button>
                      ),
                    },
                    {
                      title: 'タイプ',
                      dataIndex: 'workType',
                      key: 'workType',
                      width: 120,
                      render: (type: string) => {
                        const typeLabels: Record<string, string> = {
                          maintenance: '定期保全',
                          repair: '修理',
                          inspection: '点検',
                          emergency: '緊急',
                        };
                        const typeColors: Record<string, string> = {
                          maintenance: 'blue',
                          repair: 'orange',
                          inspection: 'green',
                          emergency: 'red',
                        };
                        return (
                          <Tag color={typeColors[type] || 'default'}>
                            {typeLabels[type] || type}
                          </Tag>
                        );
                      },
                    },
                    {
                      title: 'ステータス',
                      dataIndex: 'status',
                      key: 'status',
                      width: 120,
                      render: (status: string) => {
                        const statusLabels: Record<string, string> = {
                          scheduled: '予定',
                          in_progress: '実行中',
                          pending_approval: '承認待ち',
                          completed: '完了',
                          cancelled: 'キャンセル',
                          postponed: '延期',
                        };
                        const statusColors: Record<string, string> = {
                          scheduled: 'blue',
                          in_progress: 'orange',
                          pending_approval: 'gold',
                          completed: 'green',
                          cancelled: 'red',
                          postponed: 'volcano',
                        };
                        return (
                          <Tag color={statusColors[status] || 'default'}>
                            {statusLabels[status] || status}
                          </Tag>
                        );
                      },
                    },
                      {
                        title: '担当者',
                        dataIndex: 'assignedTo',
                        key: 'assignedTo',
                        width: 150,
                        render: (_userId: string | null, record: ScheduleHistoryRow) => {
                          const assignedUsers = (record.assignedUsers ?? []).filter(Boolean);
                          if (assignedUsers.length > 0) {
                            return (
                              <Space size="small" wrap>
                                {assignedUsers.map((user) => (
                                  <UserDisplay
                                    key={user.id}
                                    userId={user.id}
                                    user={user}
                                    size="small"
                                    showAvatar={false}
                                  />
                                ))}
                              </Space>
                            );
                          }
                          const userId = record.assignedTo;
                          if (!userId) return '-';
                          const fallbackName = userNameMap.get(userId);
                          return (
                            <UserDisplay
                              userId={userId}
                              user={fallbackName ? { id: userId, name: fallbackName } : undefined}
                              size="small"
                              showAvatar={false}
                            />
                          );
                        },
                      },
                    {
                      title: '実施日',
                      dataIndex: 'actualStart',
                      key: 'actualStart',
                      width: 120,
                      render: (date: string) => formatDateWithFallback(date),
                    },
                  ]}
                  onRow={(record: ScheduleHistoryRow) => ({
                    onClick: () => navigate(`/app/schedule/${record.id}`),
                    style: { cursor: 'pointer' },
                  })}
                />
              </Card>
            ),
          },
          {
            key: '6',
            label: '書類',
            children: (
              <DocumentListSection
                entityType="equipment"
                entityId={equipmentId}
                allowCreate={true}
              />
            ),
          },
        ]}
      />

      {/* 共通モーダル（実使用） */}
      <EquipmentFormModal
        open={isEditModalVisible}
        title="設備編集"
        currentId={equipmentId}
        confirmLoading={updateMutation.isPending}
        initialValues={{
          ...equipment,
          dpEulType: isEulType(equipment.dpEulType) ? equipment.dpEulType : DEFAULT_EUL_TYPE,
          category: equipment.categoryId ?? null,
          commissionedDate: equipment.commissionedDate ? dayjs(equipment.commissionedDate) : null,
          installationDate: equipment.installationDate ? dayjs(equipment.installationDate) : null,
          locationIds: equipmentLocationEntries.map((entry) => entry.locationId),
        }}
        onCancel={() => setIsEditModalVisible(false)}
        onSubmit={async ({ values, locationIds }) => {
          await updateMutation.mutateAsync({ id: equipmentId, data: values });
          if (locationIds && locationIds.length > 0) {
            await updateLocationsMutation.mutateAsync({ equipmentId, locationIds });
          }
          setIsEditModalVisible(false);
        }}
      />
    </div>
  );
}





